"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShopifyStoreUsageIndicator } from "@/components/agents/shared/shopify-store-usage-indicator";
import { useShopifyDomainUsage } from "@/hooks/use-shopify-domain-usage";

/**
 * Shop Domain field for email/messaging agent settings.
 *
 * Lets the admin pin a specific Shopify store for the agent's Catalog and
 * Policies tools. Two modes:
 *   1. Dropdown pick from the tenant's active API-enabled integrations
 *   2. Manual entry of an arbitrary `*.myshopify.com` domain (for stores that
 *      aren't connected via OAuth but still answer the public Storefront MCP
 *      endpoint)
 *
 * Custom mode is a **separate UI**, not a sentinel value inside the Select.
 * Earlier versions tried to put a "Custom domain…" item inside the Radix
 * Select, but Radix's controlled-value machinery fought back and reverted the
 * selection on the next render. Keeping custom mode out of the Select sidesteps
 * that entirely.
 *
 * Sentinel `null` = auto-detect. We use `null` (not `undefined`) because JSON
 * PATCH endpoints drop `undefined` fields and we need a way to explicitly
 * clear a previously set value. Format validation happens on the tool execute
 * path via `normalizeShopDomain()` — we do NOT validate on save to avoid
 * throwing on legacy/dirty rows.
 */

interface ShopifyIntegrationOption {
  shop_domain: string;
  is_active?: boolean;
  uses_api?: boolean;
}

interface ShopDomainFieldProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  integrations: ShopifyIntegrationOption[];
  disabled?: boolean;
  /**
   * Parent's save-in-flight flag. The field watches this for `true → false`
   * transitions and refetches `useShopifyDomainUsage` so the inline
   * `N / M stores` indicator reflects the new pin (or pin removal) without
   * requiring a manual collapse/expand of the section. Optional — when
   * omitted, the indicator only reflects mount-time state plus the next
   * full UI re-mount.
   */
  isSaving?: boolean;
}

const AUTO_SENTINEL = "__auto__";

/**
 * Lightweight client-side validator for manual custom-domain entries.
 *
 * This catches the typical typo class (missing `.myshopify.com` suffix, bad
 * hostname chars) and surfaces an inline error in the UI. It is **not** the
 * authoritative SSRF validator — that's `normalizeShopDomain()` on the tool
 * execute path. This regex intentionally does NOT try to replicate
 * `normalizeShopDomain`'s full behavior (protocol stripping, path stripping);
 * we show an error instead of silently reformatting, so the admin sees exactly
 * what will be stored.
 */
const SHOPIFY_DOMAIN_FORMAT = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i;

const isKnownActiveIntegration = (
  domain: string,
  integrations: ShopifyIntegrationOption[],
): boolean =>
  integrations.some(
    (i) => i.shop_domain === domain && i.is_active && i.uses_api,
  );

export function ShopDomainField({
  value,
  onChange,
  integrations,
  disabled,
  isSaving,
}: ShopDomainFieldProps) {
  // Live store-count usage from server. Drives the inline `N / M stores`
  // badge and the `allowedToAdd` UX shortcut for custom domains.
  const usage = useShopifyDomainUsage();
  // Extract `refresh` separately so the effect below has a stable dep —
  // `usage` itself is a fresh object on every render.
  const { refresh: refreshUsage } = usage;

  // Refetch usage whenever the parent finishes a save (`isSaving`
  // transitions from `true → false`). This keeps the `N / M stores`
  // indicator in sync after the admin adds or removes a custom pin —
  // otherwise the badge stays stuck on the mount-time count and the
  // user has to collapse/expand the Shopify Tools section to see the
  // new value. Re-fetches on any save in the parent (not just the
  // shop-domain field) — that's intentional: extra refetches are cheap
  // and keep the indicator fresh under any kind of agent config edit.
  const wasSavingRef = useRef(false);
  useEffect(() => {
    if (wasSavingRef.current && !isSaving) {
      refreshUsage();
    }
    wasSavingRef.current = Boolean(isSaving);
  }, [isSaving, refreshUsage]);

  // Derived: is the saved value a manually-entered domain (not in the active
  // integrations list)? If so, we render custom mode on mount.
  const savedValueIsCustom =
    !!value && !isKnownActiveIntegration(value, integrations);

  // Tracks whether the user clicked "Enter custom domain" to opt into custom
  // mode even though no custom value is saved yet. OR'd with `savedValueIsCustom`.
  const [userOptedIntoCustom, setUserOptedIntoCustom] = useState(false);

  // Local draft for the custom input. Initialized from the saved value if it
  // was already a custom domain.
  const [customDraft, setCustomDraft] = useState<string>(() =>
    savedValueIsCustom ? (value as string) : "",
  );

  // Client-side format-error message for manual custom entry. Set on invalid
  // commit, cleared when the user edits or exits custom mode.
  const [customError, setCustomError] = useState<string | null>(null);

  const customMode = userOptedIntoCustom || savedValueIsCustom;

  // Safe value for the Radix Select: only AUTO or a known integration domain.
  // Never `null` or a custom string that Radix doesn't have as a SelectItem.
  const selectValue =
    value && !savedValueIsCustom && isKnownActiveIntegration(value, integrations)
      ? value
      : AUTO_SENTINEL;

  const handleSelectChange = (next: string) => {
    if (next === AUTO_SENTINEL) {
      onChange(null);
      return;
    }
    // Picked a known integration domain.
    onChange(next);
  };

  const enterCustomMode = () => {
    setUserOptedIntoCustom(true);
    // Seed the draft with whatever's currently saved (might be empty for
    // auto-detect, or a domain if user is switching from an integration pick).
    setCustomDraft(value ?? "");
  };

  const exitCustomMode = () => {
    setUserOptedIntoCustom(false);
    setCustomDraft("");
    setCustomError(null);
    // If a custom value was saved, clear it; otherwise the user was only in a
    // draft state and nothing needs to change in the parent.
    if (savedValueIsCustom) {
      onChange(null);
    }
  };

  const commitCustomDraft = () => {
    const trimmed = customDraft.trim();
    if (trimmed.length === 0) {
      // Empty input → treat as "cancel, go back to auto-detect".
      exitCustomMode();
      return;
    }
    if (trimmed === value) {
      setCustomError(null);
      return;
    }
    // Format guard — surface typos inline instead of failing silently on the
    // execute path. Authoritative SSRF validation still happens in
    // normalizeShopDomain() at tool call time.
    if (!SHOPIFY_DOMAIN_FORMAT.test(trimmed)) {
      setCustomError(
        "Enter a valid Shopify domain, e.g. your-store.myshopify.com",
      );
      return;
    }
    // Plan-level store-count UX shortcut: if adding this domain would push the
    // tenant over `shopify_stores_limit`, show inline error instead of letting
    // the PATCH 403. Authoritative check still lives on the server.
    if (!usage.loading && !usage.allowedToAdd(trimmed)) {
      setCustomError(
        `Shop domain limit reached (${usage.count}/${usage.limit}). Remove an existing pin or upgrade your plan.`,
      );
      return;
    }
    setCustomError(null);
    onChange(trimmed);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium text-gray-700">
          Shop Domain (optional)
        </Label>
        <ShopifyStoreUsageIndicator
          count={usage.count}
          limit={usage.limit}
          domains={usage.domains}
          loading={usage.loading}
        />
      </div>

      {customMode ? (
        <div className="space-y-1">
          <div className="flex gap-1.5">
            <Input
              value={customDraft}
              onChange={(e) => {
                setCustomDraft(e.target.value);
                // Clear error as soon as the user starts editing — stale
                // error messages on a value they're already fixing are noise.
                if (customError) setCustomError(null);
              }}
              onBlur={commitCustomDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitCustomDraft();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  exitCustomMode();
                }
              }}
              placeholder="your-store.myshopify.com"
              disabled={disabled}
              aria-invalid={customError ? true : undefined}
              className={
                customError
                  ? "h-9 text-xs bg-white flex-1 border-red-300 focus-visible:ring-red-400"
                  : "h-9 text-xs bg-white flex-1"
              }
              autoFocus
            />
            <button
              type="button"
              onClick={exitCustomMode}
              disabled={disabled}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50"
              aria-label="Cancel custom domain"
              title="Use integrations list instead"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {customError ? (
            <p className="text-[11px] text-red-600" role="alert">
              {customError}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Enter a `*.myshopify.com` domain and press Enter or click outside
              to save.
            </p>
          )}
        </div>
      ) : (
        <>
          <Select
            value={selectValue}
            onValueChange={handleSelectChange}
            disabled={disabled}
          >
            <SelectTrigger className="w-full h-9 text-xs bg-white">
              <SelectValue placeholder="Auto-detect (use any active store)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO_SENTINEL}>
                Auto-detect (use any active store)
              </SelectItem>
              {integrations
                .filter((i) => i.is_active && i.uses_api)
                .map((i) => (
                  <SelectItem key={i.shop_domain} value={i.shop_domain}>
                    {i.shop_domain}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={enterCustomMode}
            disabled={disabled}
            className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
          >
            Or enter a custom domain manually
          </button>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        <span>Applies only to</span>{" "}
        <span className="font-medium">Catalog Search</span> <span>and</span>{" "}
        <span className="font-medium">Policies &amp; FAQs</span><span>. Order Lookup
        always reads orders from your connected Shopify integration and is not
        affected by this setting.</span>
      </p>
      <p className="text-xs text-muted-foreground">
        <span>Use the canonical</span>{" "}
        <code className="font-mono text-[11px]">your-store.myshopify.com</code>{" "}
        <span>address, not a custom storefront URL like</span>{" "}
        <code className="font-mono text-[11px]">your-store.com</code><span>. Every
        Shopify store has a canonical</span>{" "}
        <code className="font-mono text-[11px]">.myshopify.com</code> <span>domain
        even when a custom domain is configured — find it in Shopify Admin →</span>{" "}
        <span className="font-medium">Settings → Domains</span><span>.</span>
      </p>
    </div>
  );
}
